/**
 * One note of the feedback page (#377, #661): a card in the gutter beside the page, joined to its frame.
 *
 * - **Only what the response gives.** The title is the mark's word (and the question label, when there is one); the body is the server's
 *   own wording. Nothing is added about the learner, and a verdict, a reason or a question text that the response does not carry is never drawn.
 * - **Help is shown only as the server recorded it.** The rungs are the response's own (each recorded before it was sent), and each pull button
 *   has its own cost line beside it (derived from the rung's kind, #438), read before it is pressed. What a rung opened is drawn inside the note;
 *   nothing behind a rung is hidden or gated here, because it is not in the response until the rung is asked for.
 * - **Invariant 3.** "How this was checked" stays at the foot of every note that has anything to say: the lines as they were read (R2: the learner
 *   sees what was checked, and a line they typed themselves says so), the certificate, and each course rule quoted with its source (#361, #366).
 *   A rule the check needed and the course materials have not been shown to state is named as such: that is why the line is not checked. Where
 *   the note has no frame on the page, the reading IS the location: the card says why and quotes it.
 * - **A reviewer's pointer** (the channel, `flag: check_this`) is a look-here note, titled "Check this line", with a chip saying it is a pointer and
 *   not a verdict. Its "Why?" is a rung with its cost, and the reason is the model's text, drawn as plain text.
 *
 * All learner and course text goes through `UntrustedText`/`HandwrittenText`: Hebrew phrases isolated right-to-left, everything else drawn
 * strictly in stored order, so a maths line is never shown reordered (`e2e/bidi.spec.ts`).
 */

import type { HomeworkFeedbackResponse } from '@davinci/contracts';
import { fillTemplate } from '../../i18n/fill-template.js';
import { useFeedbackLabels, type PullableRung } from '../../i18n/homework-feedback-labels.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { localizedNoteSentence } from '../../i18n/note-sentences.js';
import { noteId, type BoardNote } from './feedback-blocks.js';
import { DisputeControl } from './dispute-control.js';
import type { ReasonState } from './feedback-channel.js';
import { feedbackChrome } from './feedback-chrome.js';
import { kindGlyph } from './feedback-frames.js';
import { FixedMark, type FixedEntry } from './fixed-mark.js';
import { HandwrittenText, UntrustedText } from './untrusted-text.js';

type Note = HomeworkFeedbackResponse['notes'][number];
/** One line as a reader read it: the id of the line, the text, and whether the learner typed it themselves. */
type ReadingOf = { readonly regionId: string; readonly text: string; readonly learnerCorrected: boolean };

export interface FeedbackNoteCardProps {
  readonly entry: BoardNote;
  readonly run: NonNullable<HomeworkFeedbackResponse['run']>;
  /** Pulls one rung for this note's claim; absent when nothing can be pulled. */
  readonly onPull?: (rung: PullableRung) => void;
  /** A pull for this note is on its way. */
  readonly pulling?: boolean;
  /** Set when this ✓ is the fix of a mistake on an earlier upload of the same sheet (#415). */
  readonly fixed?: FixedEntry;
  /** Flags this note's verdict for review; absent when nothing can be flagged. */
  readonly onDispute?: () => Promise<void>;
  readonly selected?: boolean;
  /** The title was pressed: the frame is highlighted. */
  readonly onSelect?: (id: string) => void;
  /** For a reviewer's pointer: what the learner has done with "Why?", and the press. */
  readonly reason?: ReasonState | undefined;
  readonly onWhy?: () => void;
  /** The note is a sheet over the page (the phone), not a card on it. */
  readonly bare?: boolean;
  /** Only "How this was checked", with no card: a line that was only read, in an opened run on a phone, as the board draws it. */
  readonly howOnly?: boolean;
}

/** The DOM id of a note's card, so that a frame's mark can bring the focus to it. */
export const noteDomId = (id: string): string => `feedback-note-${id}`;

export function FeedbackNoteCard({ entry, run, onPull, pulling = false, fixed, onDispute, selected = false, onSelect, reason, onWhy, bare = false, howOnly = false }: FeedbackNoteCardProps): React.JSX.Element {
  const t = useFeedbackLabels();
  const h = useHomeworkLabels();
  const m = useMessages(feedbackChrome);
  const { locale } = useLocale();
  const id = noteDomId(entry.id);
  const only: Note | undefined = entry.members.length === 1 ? entry.members[0] : undefined;
  const isFlag = entry.flag !== undefined;
  const word = isFlag ? t.channelFlagDefault : t.markWord(entry.mark as Note['mark']);
  const typed = new Set(only?.learnerCorrectedRegionIds ?? []);
  const quoted = only?.location?.kind === 'quote' ? only.location.reason : entry.flag?.location?.kind === 'quote' ? entry.flag.location.reason : undefined;
  const source = only?.rules.find((rule) => rule.standing === 'used' && rule.quote !== null);
  const costId = `feedback-cost-${entry.id}`;
  const framed = entry.blockBbox !== null;
  const repeat = entry.repeat;
  // What this conclusion is about (invariant 3) is drawn in the note itself, not behind a closed disclosure: for a decided note (✓ ✗, and a rule-ruled
  // ✓) and for a reviewer's pointer. A merged read-not-checked block keeps its lines in "How this was checked"; a note with no frame quotes its lines below.
  const decided = only !== undefined && (entry.kind === 'right' || entry.kind === 'wrong');
  const readAs: readonly ReadingOf[] = !framed ? [] : decided ? only.readings : entry.flagReading !== undefined ? [entry.flagReading] : [];

  if (howOnly) {
    return (
      <div className="fb-how-only">
        <HowChecked entry={entry} run={run} showReadings={framed} typed={typed} />
      </div>
    );
  }

  return (
    <article
      id={id}
      className={`fb-note feedback-note${only === undefined ? '' : ` feedback-note-${only.mark}`}`}
      data-kind={entry.kind}
      data-mark={entry.mark}
      data-selected={selected}
      data-bare={bare}
      data-hit
      aria-labelledby={`${id}-title`}
    >
      <h4 id={`${id}-title`} className="fb-note-head">
        <button type="button" className="fb-note-title" aria-pressed={selected} onClick={() => onSelect?.(entry.id)}>
          <span aria-hidden="true" className="fb-note-glyph">{kindGlyph(entry)}</span>
          <span>
            {entry.questionLabel !== null && !isFlag ? (
              <>
                <UntrustedText text={entry.questionLabel} />:{' '}
              </>
            ) : null}
            {only?.mark === 'under_review' ? <span className="feedback-chip">{t.underReviewChip}</span> : word}
          </span>
        </button>
      </h4>
      {isFlag ? <span className="fb-pill fb-pill-look">{m.pointerNotVerdict}</span> : null}

      {only !== undefined ? <p dir="auto" className="fb-note-body">{localizedNoteSentence(only.text, locale)}</p> : null}
      {entry.merged && !repeat ? <p className="fb-note-body">{m.uncheckedSummary}</p> : null}
      {readAs.length > 0 ? (
        <div className="fb-read-as">
          <span className="fb-read-as-label">{m.readAs}</span>
          {readAs.map((reading) => (
            <span key={reading.regionId} className={only?.errorRegionIds.includes(reading.regionId) === true ? 'feedback-reading-error' : undefined}>
              <HandwrittenText text={reading.text} />
              {typed.has(reading.regionId) || reading.learnerCorrected ? <span className="field-hint">({t.learnerTyped})</span> : null}
            </span>
          ))}
        </div>
      ) : null}
      {only !== undefined && fixed !== undefined ? <FixedMark entry={fixed} /> : null}
      {only?.mark === 'under_review' && only.ruleBy !== null ? <p className="field-hint">{t.ruleBy(h.dueDate(only.ruleBy))}</p> : null}

      {source !== undefined ? (
        <p className="fb-note-source">
          {m.courseSource} <UntrustedText text={source.moduleName ?? ''} />, {t.rulePage(String(source.page ?? ''))}
          {source.where !== null ? (
            <>
              , <UntrustedText text={source.where} />
            </>
          ) : null}
        </p>
      ) : null}

      {/* Where the note has no frame, its lines are the location: they are drawn here, with why there is no frame. */}
      {!framed && only !== undefined && only.readings.length > 0 ? (
        <div className="feedback-readings">
          {quoted !== undefined ? <p className="field-hint">{t.quoteReason(quoted)}</p> : null}
          <blockquote>
            {only.readings.map((reading) => (
              <div key={reading.regionId} className={only.errorRegionIds.includes(reading.regionId) ? 'feedback-reading-error' : undefined}>
                <HandwrittenText text={reading.text} />
                {typed.has(reading.regionId) || reading.learnerCorrected ? <p className="field-hint">({t.learnerTyped})</p> : null}
              </div>
            ))}
          </blockquote>
        </div>
      ) : null}
      {/* An unframed flag points at a line by quoting it: the hint says so, so the line is drawn under it. */}
      {!framed && isFlag && entry.flagReading !== undefined ? (
        <div className="feedback-readings">
          {quoted !== undefined ? <p className="field-hint">{t.quoteReason(quoted)}</p> : null}
          <blockquote>
            <HandwrittenText text={entry.flagReading.text} />
          </blockquote>
        </div>
      ) : !framed && isFlag && quoted !== undefined ? (
        <p className="field-hint">{t.quoteReason(quoted)}</p>
      ) : null}

      {only !== undefined && only.rungs.length > 0 ? (
        <dl className="feedback-rungs">
          {only.rungs.map((rung) => (
            <div key={`${rung.rung}-${rung.deliveredAt}`} className="fb-rung" data-open="true">
              <dt>{t.rungWord(rung.rung, rung.solutionSource)}</dt>
              <dd>{rung.rung === 'error_location' || rung.rung === 'hint' ? localizedNoteSentence(rung.text, locale) : rung.text}</dd>
              {rung.rung === 'solution' && rung.solutionSource === 'davinci' ? (
                // DaVinci's own worked solution (#586): generated, so it says it is not official and can be wrong.
                <dd className="field-hint feedback-solution-note">{t.solutionNotOfficial}</dd>
              ) : null}
            </div>
          ))}
        </dl>
      ) : null}

      {only !== undefined && (only.pullable.length > 0 || only.solutionOpensAt !== null) && onPull !== undefined && only.mark !== 'under_review' && only.mark !== 'ruled_correct' ? (
        <div className="feedback-pull">
          {only.pullable.map((rung) => (
            <div key={rung} className="feedback-rung">
              <button type="button" className="button-quiet" disabled={pulling} aria-describedby={`${costId}-${rung}`} onClick={() => onPull(rung)}>
                {t.pullAction(rung)}
              </button>
              <p aria-hidden="true" className="field-hint">
                {t.rungCostShort(rung)}
              </p>
              <span id={`${costId}-${rung}`} className="visually-hidden">
                {t.rungCost(rung)}
              </span>
            </div>
          ))}
          {only.solutionOpensAt !== null ? (
            // The worked solution before its due date (#480): shown so the learner knows it exists and when, never pullable.
            <div key="solution-closed" className="feedback-rung">
              <button type="button" className="button-quiet" disabled aria-describedby={`${costId}-solution`}>
                {t.solutionOpens(only.solutionOpensAt)}
              </button>
              <p aria-hidden="true" className="field-hint">
                {t.rungCostShort('solution')}
              </p>
              <span id={`${costId}-solution`} className="visually-hidden">
                {t.rungCost('solution')}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {entry.flag !== undefined ? (
        <>
          {entry.flag.reasonAvailable && reason?.status !== 'shown' ? (
            <div className="feedback-pull">
              <div className="feedback-rung">
                <button type="button" className="button-quiet" disabled={reason?.status === 'loading'} aria-describedby={`${costId}-why`} onClick={onWhy}>
                  {t.channelWhyAction}
                </button>
                <p id={`${costId}-why`} className="field-hint">
                  {t.channelWhyCost}
                </p>
              </div>
            </div>
          ) : null}
          {reason?.status === 'loading' ? <p role="status">{t.channelReasonLoading}</p> : null}
          {reason?.status === 'failed' ? (
            <p role="alert" className="field-hint">
              {t.channelReasonFailed}
            </p>
          ) : null}
          {reason?.status === 'shown' ? (
            <div className="fb-rung" data-open="true">
              <p className="channel-reason" dir="auto">
                <UntrustedText text={reason.reason} />
              </p>
              <p className="field-hint">{t.channelWhyCost}</p>
            </div>
          ) : null}
        </>
      ) : null}

      {only !== undefined && only.mark === 'refuted' && only.examPrepNoted ? <span className="feedback-chip fb-chip-look">{t.examPrepNoted}</span> : null}

      <div className="fb-foot">
        <HowChecked entry={entry} run={run} showReadings={framed && readAs.length === 0} typed={typed} />
        {only !== undefined && only.disputable && onDispute !== undefined ? <DisputeControl onConfirm={onDispute} /> : null}
      </div>
    </article>
  );
}

/**
 * "How this was checked", the note's route to its evidence (invariant 3). A note with nothing to say (a reviewer's pointer, which concludes
 * nothing) draws nothing. Lines read and not checked, folded into one block, keep every line's own wording and reading here.
 */
function HowChecked({ entry, run, showReadings, typed }: { readonly entry: BoardNote; readonly run: NonNullable<HomeworkFeedbackResponse['run']>; readonly showReadings: boolean; readonly typed: ReadonlySet<string> }): React.JSX.Element | null {
  const t = useFeedbackLabels();
  const { locale } = useLocale();
  const only = entry.members.length === 1 ? entry.members[0] : undefined;
  if (only?.mark === 'ruled_correct') return null;
  const certified = only !== undefined && only.certificate !== null && only.mark !== 'under_review';
  const rules = entry.members.flatMap((member) => member.rules);
  const hasReadings = entry.members.some((member) => member.readings.length > 0);
  if (!certified && rules.length === 0 && !(hasReadings && (showReadings || entry.merged))) return null;
  return (
    <details className="feedback-certificate fb-certificate">
      <summary>{t.certificateSummary}</summary>
      {entry.merged ? (
        <ul className="plain-list fb-folded">
          {entry.members.map((member) => (
            <li key={noteId(member)}>
              <p dir="auto">{localizedNoteSentence(member.text, locale)}</p>
              {member.readings.length > 0 ? (
                <blockquote>
                  {member.readings.map((reading) => (
                    <div key={reading.regionId}>
                      <HandwrittenText text={reading.text} />
                      {member.learnerCorrectedRegionIds.includes(reading.regionId) || reading.learnerCorrected ? <p className="field-hint">({t.learnerTyped})</p> : null}
                    </div>
                  ))}
                </blockquote>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {showReadings && only !== undefined && only.readings.length > 0 ? (
        <div className="feedback-readings">
          <blockquote>
            {only.readings.map((reading) => (
              <div key={reading.regionId} className={only.errorRegionIds.includes(reading.regionId) ? 'feedback-reading-error' : undefined}>
                <HandwrittenText text={reading.text} />
                {typed.has(reading.regionId) || reading.learnerCorrected ? <p className="field-hint">({t.learnerTyped})</p> : null}
              </div>
            ))}
          </blockquote>
        </div>
      ) : null}
      {rules.map((rule) =>
        rule.standing === 'not_sourced' ? (
          <p key={rule.ruleId} className="feedback-rule">
            {t.ruleNotSourced(rule.ruleId)}
          </p>
        ) : rule.quote === null ? (
          <p key={rule.ruleId} className="feedback-rule">
            {t.ruleUnquoted(rule.ruleId)}
          </p>
        ) : (
          <figure key={rule.ruleId} className="feedback-rule">
            <figcaption>{t.ruleUsedIntro}</figcaption>
            <blockquote>
              <HandwrittenText text={rule.quote} />
            </blockquote>
            <p className="field-hint">
              <UntrustedText text={rule.moduleName ?? ''} />, {t.rulePage(String(rule.page ?? ''))}
              {rule.where !== null ? (
                <>
                  , <UntrustedText text={rule.where} />
                </>
              ) : null}{' '}
              (<code>{rule.ruleId}</code>)
            </p>
          </figure>
        ),
      )}
      {certified && only !== undefined && only.certificate !== null ? (
        <p>
          {fillTemplate(t.certificateLine, {
            words: t.certificateWord(only.certificate.kind),
            kind: <bdi dir="ltr"><code>{only.certificate.kind}</code></bdi>,
            check: <bdi dir="ltr"><code>{only.certificate.checkId}</code></bdi>,
            kernel: <bdi dir="ltr"><code>{run.kernelVersion}</code></bdi>,
          })}
        </p>
      ) : null}
    </details>
  );
}
